/**
 * SaringanTab - Screening/intake tab for daily log form
 */
import { Clock, Thermometer, Package, User } from "lucide-react";

const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

export default function SaringanTab({ formData, updateFormField }) {
  return (
    <div className="space-y-4 p-3 bg-purple-50 rounded-lg">
      {/* Waktu Tiba */}
      <div>
        <label className="block text-sm font-medium text-purple-800 mb-2">
          <Clock className="w-4 h-4 inline mr-1" /> Waktu Tiba
        </label>
        <div className="flex flex-wrap gap-2">
          <select
            value={formData.status_saringan?.waktu_tiba?.hour || ''}
            onChange={(e) => updateFormField('status_saringan.waktu_tiba.hour', e.target.value)}
            className="flex-1 min-w-[70px] max-w-[90px] px-2 py-2 border border-purple-200 rounded-lg text-sm"
          >
            <option value="">Jam</option>
            {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
          <select
            value={formData.status_saringan?.waktu_tiba?.minute || ''}
            onChange={(e) => updateFormField('status_saringan.waktu_tiba.minute', e.target.value)}
            className="flex-1 min-w-[70px] max-w-[90px] px-2 py-2 border border-purple-200 rounded-lg text-sm"
          >
            <option value="">Minit</option>
            {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select
            value={formData.status_saringan?.waktu_tiba?.period || 'AM'}
            onChange={(e) => updateFormField('status_saringan.waktu_tiba.period', e.target.value)}
            className="flex-1 min-w-[60px] max-w-[80px] px-2 py-2 border border-purple-200 rounded-lg text-sm"
          >
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </div>
      </div>

      {/* Penghantar */}
      <div>
        <label className="block text-sm font-medium text-purple-800 mb-2">
          <User className="w-4 h-4 inline mr-1" /> Penghantar
        </label>
        <div className="flex flex-wrap gap-2">
          {['ibu_bapa', 'keluarga', 'penjaga', 'perkhidmatan'].map(type => (
            <label key={type} className="flex items-center gap-1 bg-white px-3 py-1 rounded-lg border cursor-pointer hover:bg-purple-50">
              <input
                type="checkbox"
                checked={formData.status_saringan?.penghantar?.[type] || false}
                onChange={(e) => updateFormField(`status_saringan.penghantar.${type}`, e.target.checked)}
                className="rounded"
              />
              <span className="text-sm capitalize">{type.replace('_', ' ')}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Status Kesihatan */}
      <div>
        <label className="block text-sm font-medium text-purple-800 mb-2">
          <Thermometer className="w-4 h-4 inline mr-1" /> Status Kesihatan
        </label>
        <div className="flex gap-4 items-center">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="kesihatan"
              value="sihat"
              checked={formData.status_saringan?.status_kesihatan?.sihat === 'sihat'}
              onChange={(e) => updateFormField('status_saringan.status_kesihatan.sihat', e.target.value)}
              className="text-green-500"
            />
            <span className="text-green-700">Sihat</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="kesihatan"
              value="kurang_sihat"
              checked={formData.status_saringan?.status_kesihatan?.sihat === 'kurang_sihat'}
              onChange={(e) => updateFormField('status_saringan.status_kesihatan.sihat', e.target.value)}
              className="text-orange-500"
            />
            <span className="text-orange-700">Kurang Sihat</span>
          </label>
          <input
            type="number"
            step="0.1"
            placeholder="Suhu"
            value={formData.status_saringan?.status_kesihatan?.suhu_badan || ''}
            onChange={(e) => updateFormField('status_saringan.status_kesihatan.suhu_badan', e.target.value)}
            className="w-24 px-3 py-1 border border-purple-200 rounded-lg text-sm"
          />
        </div>
      </div>

      {/* Sudah Mandi */}
      <div>
        <label className="block text-sm font-medium text-purple-800 mb-2">Sudah Mandi?</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mandi"
              value="ya"
              checked={formData.status_saringan?.sudah_mandi === 'ya'}
              onChange={(e) => updateFormField('status_saringan.sudah_mandi', e.target.value)}
            />
            <span>Ya</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="mandi"
              value="tidak"
              checked={formData.status_saringan?.sudah_mandi === 'tidak'}
              onChange={(e) => updateFormField('status_saringan.sudah_mandi', e.target.value)}
            />
            <span>Tidak</span>
          </label>
        </div>
      </div>

      {/* Alatan Dalam Beg */}
      <div>
        <label className="block text-sm font-medium text-purple-800 mb-2">
          <Package className="w-4 h-4 inline mr-1" /> Alatan Dalam Beg
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { key: 'botol_susu', label: 'Botol Susu' },
            { key: 'lampin', label: 'Lampin' },
            { key: 'botol_air', label: 'Botol Air' },
            { key: 'tuala_mandi', label: 'Tuala Mandi' },
            { key: 'pakaian_bersih', label: 'Pakaian Bersih' },
            { key: 'bekas_baju_kotor', label: 'Bekas Baju Kotor' }
          ].map(item => (
            <div key={item.key} className="flex items-center gap-2 bg-white px-2 py-1 rounded border">
              <span className="text-xs flex-1">{item.label}</span>
              <input
                type="number"
                min="0"
                max="10"
                value={formData.status_saringan?.alatan_dalam_beg?.[item.key] || ''}
                onChange={(e) => updateFormField(`status_saringan.alatan_dalam_beg.${item.key}`, e.target.value)}
                className="w-12 px-2 py-1 border rounded text-center text-sm"
                placeholder="0"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
